/**
 * The locale selector's model (Q2) and the direction rules (AM-15.3).
 *
 * The *list* of locales always comes from the API payload — D22 promises that
 * adding a language needs no admin change, and a constant here
 * would quietly break that promise. What admin supplies is the presentation
 * metadata for a code it has been handed: its endonym and its script direction,
 * both from `@atsready/shared/i18n`, which is the same tuple the publish gate
 * reads.
 */

import {
  LANGUAGE_DIRECTION,
  LANGUAGE_DISPLAY_NAMES,
  LANGUAGE_NATIVE_NAMES,
  SUPPORTED_UI_LANGUAGES,
  type TextDirection,
  type UiLanguage,
} from '@atsready/shared/i18n';
import type { BlogLocaleEntry } from './types';

function isKnownLanguage(code: string): code is UiLanguage {
  return (SUPPORTED_UI_LANGUAGES as readonly string[]).includes(code);
}

/**
 * A locale's script direction.
 *
 * Every shipped language is left-to-right today, so this reads as a constant —
 * but the direction stays derived from `LANGUAGE_DIRECTION` rather than written
 * out here, because that map is the one place a right-to-left language is
 * restored as a data row rather than a code change.
 *
 * Unknown codes fall back to `ltr` rather than throwing: the API is allowed to
 * add a language before admin's shared dependency is bumped, and a selector
 * that crashed on the new code would be worse than one that renders it LTR.
 * That fallback is now the only reason this is a function.
 */
export function directionFor(code: string): TextDirection {
  return isKnownLanguage(code) ? LANGUAGE_DIRECTION[code] : 'ltr';
}

/** The endonym, or the bare code when admin has no name for it. */
export function nativeNameFor(code: string): string {
  return isKnownLanguage(code) ? LANGUAGE_NATIVE_NAMES[code] : code;
}

/** The English name, used where the surrounding sentence is English. */
export function englishNameFor(code: string): string {
  return isKnownLanguage(code) ? LANGUAGE_DISPLAY_NAMES[code] : code;
}

export interface LocaleOption {
  code: string;
  label: string;
  written: boolean;
}

export interface LocaleOptionGroup {
  label: string;
  options: LocaleOption[];
}

/**
 * Every locale in the publish set, grouped `Written (n)` / `Not written (rest)`.
 *
 * This supersedes the spec's "list only the locales present": the gaps in a
 * cluster are the thing a reviewer most needs to see, and a selector that hides
 * them makes a missing copy invisible until publish fails.
 */
export function buildLocaleGroups(entries: readonly BlogLocaleEntry[]): LocaleOptionGroup[] {
  const toOption = (entry: BlogLocaleEntry): LocaleOption => ({
    code: entry.locale,
    label: `${nativeNameFor(entry.locale)} — ${entry.locale}${entry.exists ? '' : ' (not written)'}`,
    written: entry.exists,
  });

  const written = entries.filter((entry) => entry.exists).map(toOption);
  const missing = entries.filter((entry) => !entry.exists).map(toOption);

  const groups: LocaleOptionGroup[] = [
    { label: `Written (${String(written.length)})`, options: written },
  ];
  if (missing.length > 0) {
    groups.push({ label: `Not written (${String(missing.length)})`, options: missing });
  }
  return groups;
}

/**
 * The locale the page should open on.
 *
 * A `?locale=` that names a language this cluster does not carry an entry for
 * is discarded rather than honoured — otherwise a stale bookmark renders an
 * empty shell with no way back.
 */
export function resolveSelectedLocale(
  entries: readonly BlogLocaleEntry[],
  requested: string | null,
): string {
  if (requested !== null && entries.some((entry) => entry.locale === requested)) {
    return requested;
  }
  const written = entries.find((entry) => entry.exists);
  if (written !== undefined) return written.locale;
  return entries[0]?.locale ?? 'en';
}

/**
 * The attributes a content-bearing control takes for a locale.
 *
 * AM-15.3: these go on the *control*, never on the fieldset. Putting `dir` on
 * the group mirrors the labels, the legend and the button row too, and admin
 * chrome is English — a copy in a right-to-left language must be edited in an
 * RTL textarea sitting inside an LTR form, not in a mirrored page.
 *
 * `lang` is the half that still varies: it is returned verbatim, never checked
 * against the roster, so a code the API added before admin's shared dependency
 * was bumped is still announced correctly to a screen reader.
 */
export function contentFieldAttrs(code: string): { dir: TextDirection; lang: string } {
  return { dir: directionFor(code), lang: code };
}

/**
 * Attributes for a span that holds a slug, a URL or a fact token.
 *
 * `dir="ltr"` alone is not enough inside an RTL paragraph: the neighbouring
 * strong-RTL runs still reorder around it. `unicode-bidi: isolate` closes the
 * span off from its surroundings so `upload-your-resume` reads left to right
 * with its punctuation where it was typed.
 *
 * No shipped UI language is right-to-left today, so this is currently inert —
 * kept anyway because a copy's own body text is not roster-bound (an editor can
 * paste a strong-RTL run into any locale's fields) and because unpicking 24
 * call sites to delete a two-property object is churn with a regression surface
 * and no benefit.
 */
export const LTR_ISOLATE_ATTRS = {
  dir: 'ltr' as const,
  style: { unicodeBidi: 'isolate' as const },
};
